// ==========================================================
// Paint Brush, Roller & Airbrush Physics Renderer
// ==========================================================

import type { Point, TapeSegment } from './MaskingTape';

export type ToolType = 'brush' | 'roller' | 'spray' | 'tape' | 'peel' | 'sponge' | 'gloss' | 'hand';

export interface StrokePoint extends Point {
  pressure?: number;
  time?: number;
}

export class PaintToolEngine {
  private lastPoint: StrokePoint | null = null;
  private midPoint: Point | null = null;

  /**
   * Begin a new brush stroke
   */
  public startStroke(x: number, y: number, pressure: number = 1.0) {
    this.lastPoint = { x, y, pressure, time: performance.now() };
    this.midPoint = { x, y };
  }

  /**
   * Draw continuous stroke based on active tool
   */
  public drawStroke(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    tool: ToolType,
    color: string,
    size: number,
    tapes: TapeSegment[],
    pressure: number = 1.0
  ) {
    if (!this.lastPoint) {
      this.startStroke(x, y, pressure);
      return;
    }

    const currentPoint: StrokePoint = { x, y, pressure, time: performance.now() };
    const currentMid: Point = {
      x: (this.lastPoint.x + currentPoint.x) / 2,
      y: (this.lastPoint.y + currentPoint.y) / 2
    };

    ctx.save();

    switch (tool) {
      case 'brush':
        this.renderBrushStroke(ctx, this.midPoint || this.lastPoint, currentMid, color, size, pressure);
        break;

      case 'roller':
        this.renderRollerStroke(ctx, this.lastPoint, currentPoint, color, size);
        break;

      case 'spray': {
        const dist = Math.hypot(currentPoint.x - this.lastPoint.x, currentPoint.y - this.lastPoint.y);
        const steps = Math.max(1, Math.floor(dist / Math.max(4, size * 0.35)));
        for (let s = 0; s <= steps; s++) {
          const t = s / steps;
          const interpPt = {
            x: this.lastPoint.x + (currentPoint.x - this.lastPoint.x) * t,
            y: this.lastPoint.y + (currentPoint.y - this.lastPoint.y) * t
          };
          this.renderSprayStroke(ctx, interpPt, color, size);
        }
        break;
      }

      case 'sponge':
        this.renderSpongeStroke(ctx, this.lastPoint, currentPoint, size);
        break;

      case 'gloss':
        this.renderGlossStroke(ctx, this.midPoint || this.lastPoint, currentMid, size);
        break;
    }

    ctx.restore();

    // Clean up under tapes if any exist
    if (tapes.length > 0 && tool !== 'sponge') {
      this.maskTapedRegions(ctx, tapes);
    }

    this.lastPoint = currentPoint;
    this.midPoint = currentMid;
  }

  /**
   * End the current stroke
   */
  public endStroke() {
    this.lastPoint = null;
    this.midPoint = null;
  }

  /**
   * 1. Fine Brush (Smooth Bezier curve with wet pigment feel)
   */
  private renderBrushStroke(
    ctx: CanvasRenderingContext2D,
    p1: Point,
    p2: Point,
    color: string,
    size: number,
    pressure: number
  ) {
    const effectiveSize = Math.max(2, size * (0.7 + pressure * 0.3));

    ctx.strokeStyle = color;
    ctx.lineWidth = effectiveSize;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    if (this.lastPoint) {
      ctx.quadraticCurveTo(this.lastPoint.x, this.lastPoint.y, p2.x, p2.y);
    } else {
      ctx.lineTo(p2.x, p2.y);
    }
    ctx.stroke();

    // Subtle wet-paint highlight overlay
    ctx.lineWidth = effectiveSize * 0.4;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.08)';
    ctx.stroke();
  }

  /**
   * 2. Wide Roller (Broad flat stroke with micro-roller stipple texture)
   */
  private renderRollerStroke(
    ctx: CanvasRenderingContext2D,
    p1: Point,
    p2: Point,
    color: string,
    size: number
  ) {
    const rollerWidth = Math.max(16, size * 2.2);

    ctx.strokeStyle = color;
    ctx.lineWidth = rollerWidth;
    ctx.lineCap = 'square';
    ctx.lineJoin = 'miter';

    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.stroke();

    // Roller stipple texture dots for realistic sponge roller look
    const dist = Math.hypot(p2.x - p1.x, p2.y - p1.y);
    const steps = Math.max(1, Math.floor(dist / 4));

    ctx.fillStyle = 'rgba(0, 0, 0, 0.04)';
    for (let i = 0; i < steps; i++) {
      const t = i / steps;
      const rx = p1.x + (p2.x - p1.x) * t + (Math.random() - 0.5) * (rollerWidth * 0.8);
      const ry = p1.y + (p2.y - p1.y) * t + (Math.random() - 0.5) * (rollerWidth * 0.8);
      ctx.beginPath();
      ctx.arc(rx, ry, Math.random() * 1.5, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  /**
   * 3. Spray Gun / Airbrush (Soft particle cloud dispersion with Gaussian falloff)
   */
  private renderSprayStroke(
    ctx: CanvasRenderingContext2D,
    p: Point,
    color: string,
    size: number
  ) {
    const radius = Math.max(14, size * 2.0);
    const density = Math.floor(radius * 2.2);

    ctx.fillStyle = color;

    for (let i = 0; i < density; i++) {
      // Gaussian distribution for natural spray cone
      const r = radius * Math.sqrt(-2 * Math.log(Math.max(0.0005, Math.random()))) * 0.48;
      const theta = Math.random() * 2 * Math.PI;

      if (r <= radius * 1.3) {
        const px = p.x + r * Math.cos(theta);
        const py = p.y + r * Math.sin(theta);
        const dotSize = Math.random() * 2.4 + 0.8;

        // Particle alpha falloff with distance
        const alpha = Math.max(0.12, 1 - (r / (radius * 1.3)));
        ctx.globalAlpha = Math.min(1.0, alpha * 0.85);

        ctx.beginPath();
        ctx.arc(px, py, dotSize, 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }

  /**
   * 4. Sponge / Thinner (Clean up / erase)
   */
  private renderSpongeStroke(
    ctx: CanvasRenderingContext2D,
    p1: Point,
    p2: Point,
    size: number
  ) {
    ctx.globalCompositeOperation = 'destination-out';
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.8)';
    ctx.lineWidth = size * 1.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    ctx.lineTo(p2.x, p2.y);
    ctx.stroke();
  }

  /**
   * 5. Gloss Topcoat (Specularity and shine)
   */
  private renderGlossStroke(
    ctx: CanvasRenderingContext2D,
    p1: Point,
    p2: Point,
    size: number
  ) {
    ctx.globalCompositeOperation = 'source-over';
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.22)';
    ctx.lineWidth = size * 1.2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.shadowColor = 'rgba(255, 255, 255, 0.5)';
    ctx.shadowBlur = 10;

    ctx.beginPath();
    ctx.moveTo(p1.x, p1.y);
    if (this.lastPoint) {
      ctx.quadraticCurveTo(this.lastPoint.x, this.lastPoint.y, p2.x, p2.y);
    } else {
      ctx.lineTo(p2.x, p2.y);
    }
    ctx.stroke();
  }

  /**
   * Erase paint that landed strictly under active masking tapes
   */
  private maskTapedRegions(ctx: CanvasRenderingContext2D, tapes: TapeSegment[]) {
    ctx.save();
    ctx.globalCompositeOperation = 'destination-out';
    ctx.lineCap = 'butt';

    for (const tape of tapes) {
      if (tape.isPeeling) continue;
      ctx.lineWidth = tape.width;
      ctx.beginPath();
      ctx.moveTo(tape.start.x, tape.start.y);
      ctx.lineTo(tape.end.x, tape.end.y);
      ctx.stroke();
    }
    ctx.restore();
  }
}
